import { usePpsAccess } from 'hooks/usePpsAccess';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import classes from './scheduleAttainment.module.scss';
import { useScheduleAttainment } from './useScheduleAttainment';

const ScheduleAttainment: React.FC = () => {
  const {
    isLoading,
    isSaving,
    date,
    formData,
    handleDateChange,
    handleRoomStatusChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleSave,
    roomList,
    roomCodeMap,
    shifts,
    visibility,
    onHide,
    isPastView
  } = useScheduleAttainment();

  const { canCreateScheduleAttainment } = usePpsAccess();
  const canCreate = canCreateScheduleAttainment();

  // Status dropdown options
  const roomStatusOptions = [
    { label: 'Y', value: 'Y' },
    { label: 'N', value: 'N' }
  ];

  // Render footer with action buttons
  const renderFooter = () => {
    return (
      <div className={classes.footer}>
        <Button
          label="Close"
          className={classes.cancelButton}
          onClick={onHide}
          disabled={isSaving || !canCreate}
        />
        <Button
          label="Save"
          className={classes.saveButton}
          onClick={handleSave}
          loading={isSaving}
          disabled={isPastView || !canCreate}
        />
      </div>
    );
  };

  return (
    <Dialog
      header={null}
      visible={visibility}
      blockScroll
      resizable={false}
      draggable={false}
      onHide={onHide}
      modal
      style={{ width: '90vw', maxWidth: '1200px' }}
      className={classes.scheduleAttainmentModal}
      footer={renderFooter()}
      closable={false}
      focusOnShow={false}
    >
      <div className={classes.header}>
        <h2>Schedule Planner</h2>
        <Calendar
          value={date}
          onChange={e => handleDateChange(e.value as Date)}
          dateFormat="mm-dd-yy"
          className={classes.datePicker}
          showIcon
          disabled={isSaving}
          tabIndex={-1}
          autoFocus={false}
          inputClassName={classes.noAutoFocus}
        />
      </div>
      <div className={classes.divider} />

      {/* Only render one of the two components based on isLoading */}
      {isLoading && (
        <div className={classes.skeletonContainer}>
          <table>
            <thead>
              <tr>
                <th className={classes.roomColumn}>Room</th>
                <th className={classes.dropdownColumn}>First</th>
                <th className={classes.timePickerColumn}>First Shift Start Override</th>
                <th className={classes.timePickerColumn}>First Shift End Override</th>
                <th className={classes.dropdownColumn}>Second</th>
                <th className={classes.timePickerColumn}>Second Shift Start Override</th>
                <th className={classes.timePickerColumn}>Second Shift End Override</th>
              </tr>
            </thead>
            <tbody>
              {Array(8)
                .fill(0)
                .map((_, index) => (
                  <tr
                    key={`skeleton-row-${index}`}
                    className={index % 2 === 1 ? classes.evenRow : ''}
                  >
                    <td>
                      <Skeleton width="80%" height="2.5rem" />
                    </td>
                    <td>
                      <Skeleton width="90%" height="2.5rem" />
                    </td>
                    <td>
                      <Skeleton width="90%" height="2.5rem" />
                    </td>
                    <td>
                      <Skeleton width="90%" height="2.5rem" />
                    </td>
                    <td>
                      <Skeleton width="90%" height="2.5rem" />
                    </td>
                    <td>
                      <Skeleton width="90%" height="2.5rem" />
                    </td>
                    <td>
                      <Skeleton width="90%" height="2.5rem" />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {!isLoading && (
        <div className={classes.tableContainer}>
          <table>
            <thead>
              <tr>
                <th className={classes.roomColumn}>Room</th>
                <th className={classes.dropdownColumn}>First</th>
                <th className={classes.timePickerColumn}>First Shift Start Override</th>
                <th className={classes.timePickerColumn}>First Shift End Override</th>
                <th className={classes.dropdownColumn}>Second</th>
                <th className={classes.timePickerColumn}>Second Shift Start Override</th>
                <th className={classes.timePickerColumn}>Second Shift End Override</th>
              </tr>
            </thead>
            <tbody>
              {roomList.map((room, index) => {
                const roomCode = roomCodeMap[room];
                return (
                  <tr key={room} className={index % 2 === 1 ? classes.evenRow : ''}>
                    <td>{room}</td>

                    {shifts.map(shift => (
                      <React.Fragment key={`${room}-${shift}`}>
                        <td>
                          <Dropdown
                            value={formData[roomCode]?.[shift]?.isRoomRunning || 'Y'}
                            options={roomStatusOptions}
                            onChange={e => handleRoomStatusChange(roomCode, shift, e.value)}
                            disabled={isSaving || isPastView || !canCreate}
                          />
                        </td>
                        <td>
                          <Calendar
                            value={
                              formData[roomCode]?.[shift]?.startTimeOverride
                                ? new Date(
                                  `2000-01-01 ${formData[roomCode][shift].startTimeOverride}`
                                )
                                : null
                            }
                            onChange={e => {
                              const date = e.value as Date;
                              if (date) {
                                const hours = date.getHours();
                                const minutes = date.getMinutes();
                                const period = hours >= 12 ? 'PM' : 'AM';
                                const hour = hours % 12 || 12;
                                const timeString = `${hour}:${minutes
                                  .toString()
                                  .padStart(2, '0')} ${period}`;
                                handleStartTimeChange(roomCode, shift, timeString);
                              }
                            }}
                            timeOnly
                            hourFormat="12"
                            disabled={
                              formData[roomCode]?.[shift]?.isRoomRunning === 'N' ||
                              isSaving ||
                              isPastView
                            }
                          />
                        </td>
                        <td>
                          <Calendar
                            value={
                              formData[roomCode]?.[shift]?.endTimeOverride
                                ? new Date(
                                  `2000-01-01 ${formData[roomCode][shift].endTimeOverride}`
                                )
                                : null
                            }
                            onChange={e => {
                              const date = e.value as Date;
                              if (date) {
                                const hours = date.getHours();
                                const minutes = date.getMinutes();
                                const period = hours >= 12 ? 'PM' : 'AM';
                                const hour = hours % 12 || 12;
                                const timeString = `${hour}:${minutes
                                  .toString()
                                  .padStart(2, '0')} ${period}`;
                                handleEndTimeChange(roomCode, shift, timeString);
                              }
                            }}
                            timeOnly
                            hourFormat="12"
                            disabled={
                              formData[roomCode]?.[shift]?.isRoomRunning === 'N' ||
                              isSaving ||
                              isPastView
                            }
                          />
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
};

export default observer(ScheduleAttainment);
